import { styled, css, keyframes } from 'styled-components'
import { getFrameShadow } from '../shadows.js'
import {
  getCanvas3DAssetUrl,
  getCanvasTextureAssetUrl,
} from '../../../assets/tiles.js'
import {
  ASSET_RELATIVE_BORDER,
  BOTTOM_ROTATION_DEGREES,
  RIGHT_ROTATION_DEGREES,
} from './CanvasTile.consts'
import { getBleed, MATERIAL_TYPES } from '@mixtiles/web-backend-shared'
import { sin } from '../../../utils/utils'
import { getSideToTileSizeRatio } from '../../../utils/bleedUtils'
import { m } from 'motion/react'

const appear = () => keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`

export const Container = styled.div`
  position: relative;
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
  ${({ $animationOnAppear }) =>
    $animationOnAppear &&
    css`
      animation: ${appear} ease 1s;
    `}

  .cropper-container-style {
    mix-blend-mode: multiply;
  }
`

export const Mask = styled.div`
  ${({ $isCropping }) =>
    !$isCropping &&
    css`
      ${({ $tileSize }) =>
        $tileSize && calculateCanvasMaskSize({ tileSize: $tileSize })}
      position: relative;
      overflow: hidden;
    `}
`

export const ImageContainer = styled(m.div)`
  position: relative;
  overflow: ${({ $isCropping }) => ($isCropping ? 'visible' : 'hidden')};
  mix-blend-mode: multiply;
`

function calculateTextureSidesSize({
  $relativeRightSideSize,
  $relativeBottomSideSize,
}) {
  // Calculate percentage of the angled sides in the asset
  const rightSideSize = sin(RIGHT_ROTATION_DEGREES) * $relativeRightSideSize
  const bottomSideSize = sin(BOTTOM_ROTATION_DEGREES) * $relativeBottomSideSize
  return `0 ${100 * rightSideSize}% ${100 * bottomSideSize}% 0;`
}

export const Canvas3D = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-image-source: url('${getCanvas3DAssetUrl()}');
  border-image-slice: 0 ${100 * ASSET_RELATIVE_BORDER.right}%
    ${100 * ASSET_RELATIVE_BORDER.bottom}% ${100 * ASSET_RELATIVE_BORDER.left}%
    fill; // "fill" adds texture across the entire image surface
  border-image-width: ${calculateTextureSidesSize};
`
export const CanvasTexture = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  border-image-source: url('${getCanvasTextureAssetUrl()}');
  border-image-slice: 0 fill; // "fill" adds texture across the entire image surface
  mix-blend-mode: multiply;
  transform: scale(1.5);
`

export const CanvasShadow = styled.div`
  position: absolute;
  right: 2px;
  bottom: 1px;
  box-shadow: ${getFrameShadow()};
`

export function getCanvasMaskClipPath({ tileSize }) {
  const { bleedWidth, bleedHeight } = getBleed({
    tileSize,
    materialType: MATERIAL_TYPES.CANVAS,
  })
  const { horizontalSideSize, verticalSideSize } = getSideToTileSizeRatio({
    tileSize,
    materialType: MATERIAL_TYPES.CANVAS,
    bleedWidth,
    bleedHeight,
  })
  const rightSideSize = 100 * sin(RIGHT_ROTATION_DEGREES) * horizontalSideSize
  const bottomSideSize = 100 * sin(BOTTOM_ROTATION_DEGREES) * verticalSideSize

  // Add more breaking points to the corners to make the mask more smooth
  const smallCornerSizePixels = 1.75
  const largeCornerSizePixels = 1.5
  const topRightCorner = `calc(${
    100 - rightSideSize
  }% - ${largeCornerSizePixels}px) 0, calc(${
    100 - rightSideSize
  }% + ${largeCornerSizePixels}px) ${largeCornerSizePixels}px, calc(100% - ${largeCornerSizePixels}px) calc(${bottomSideSize}% - ${largeCornerSizePixels}px), 100% calc(${bottomSideSize}% + ${largeCornerSizePixels}px)`
  const bottomRightCorner = `100% calc(100% - ${smallCornerSizePixels}px), calc(100% - ${smallCornerSizePixels}px) 100%`
  const bottomLeftCorner = `calc(${rightSideSize}% + ${largeCornerSizePixels}px) 100%, calc(${rightSideSize}% - ${largeCornerSizePixels}px) calc(100% - ${largeCornerSizePixels}px), ${largeCornerSizePixels}px calc(${
    100 - bottomSideSize
  }% + ${largeCornerSizePixels}px), 0 calc(${
    100 - bottomSideSize
  }% - ${largeCornerSizePixels}px)`
  return `polygon(${smallCornerSizePixels}px 0, ${topRightCorner}, ${bottomRightCorner}, ${bottomLeftCorner}, 0 ${smallCornerSizePixels}px)`
}

export function calculateCanvasMaskSize({ tileSize }) {
  return `clip-path: ${getCanvasMaskClipPath({ tileSize })};`
}
